import { useApp, useButton } from '@libs/hooks';
import { AssetsSvgMap, TBaseCalculatorData, TItem, TWidget } from '@libs/types';
import clsx from 'clsx';
import React from 'react';
import { SvgComponent } from '../../../common/Svg/SvgComponent';
import { ButtonRow } from '../../Buttons/ButtonRow';
import { SEARCH_ENABLED_ITEMS_COUNT_DEFAULT } from '../../List/utils/ListConstant';
import { FlagIcon } from '../../UI/FlagIcon/FlagIcon';
import { PopupComponent } from '../../UI/Popup/PopupComponent';
import { TextCommon } from '../../UI/Text/TextCommon';
import styles from '../Calculator.module.css';
import { ListCurrency } from '../ListCurrency';
import { ActionNameEnum, CurrencyDropdownEnum, TServerCalculationFunc } from '../utils/types';

type TCurrencyDropdownProps = {
    widget: TWidget<TBaseCalculatorData>;
    value: string;
    flagName?: string;
    name: CurrencyDropdownEnum;
    header: string;
    noFoundText?: string;
    currencyText?: string;
    buttonChangeTransferOptionLabel?: string;
    searchPlaceholder?: string;
    index: number;
    items: TItem[];
    isFrom?: boolean;
    isTopUp?: boolean;
    isDisabled: boolean;
    onServerCalculation: TServerCalculationFunc;
    searchEnabledItemsCount?: number;
};

const CurrencyDropdown = React.memo(
    ({
        widget,
        value,
        flagName,
        name,
        header,
        noFoundText,
        currencyText,
        index,
        items,
        isFrom,
        isTopUp,
        isDisabled,
        onServerCalculation,
        searchPlaceholder,
        buttonChangeTransferOptionLabel = '',
        searchEnabledItemsCount = SEARCH_ENABLED_ITEMS_COUNT_DEFAULT,
    }: TCurrencyDropdownProps) => {
        const { setPopup } = useApp();
        const { handleClickByActionName } = useButton(widget);
        const isShowSearchFunctionality = items.length >= searchEnabledItemsCount;
        const button =
            !isTopUp && !isFrom
                ? [
                      <ButtonRow
                          as="button"
                          isWidget={false}
                          key="ChangeTransferOption"
                          label={buttonChangeTransferOptionLabel}
                          iconName="ic_arrows_exchange"
                          handleClick={() => {
                              setPopup(null);
                              handleClickByActionName(ActionNameEnum.onChangeTransferOptions);
                          }}
                      />,
                  ]
                : [];

        const handleClick = () => {
            if (isDisabled) {
                return;
            }
            setPopup(
                <PopupComponent
                    fixedHeight={isShowSearchFunctionality}
                    withoutSeparator={isShowSearchFunctionality}
                    header={!isShowSearchFunctionality && header}
                    internalContent={
                        <ListCurrency
                            header={header}
                            noFoundText={noFoundText}
                            currencyText={currencyText}
                            isShowSearchFunctionality={isShowSearchFunctionality}
                            searchPlaceholder={searchPlaceholder}
                            index={index}
                            items={items}
                            handleClick={async (value) => {
                                setPopup(null);
                                await onServerCalculation(value, name, isFrom);
                            }}
                        />
                    }
                    externalContent={button}
                />,
            );
        };
        const classNames = clsx(styles.currencyDropdownBlock, isDisabled && styles.currencyDropdownBlockDisabled);
        return (
            <div className={classNames} onClick={handleClick} data-test={`calculator_currency_dropdown_${isFrom ?? ''}`}>
                <FlagIcon name={flagName ?? ''} />
                <TextCommon isWidget={false} content={value?.toUpperCase()} options={{ variant: 'main', textColor: 'black', margin: { left: 4 } }} />
                {!isDisabled && <SvgComponent width={16} height={16} fill="black" name={AssetsSvgMap.ic_chevron_down_small} />}
            </div>
        );
    },
);

export { CurrencyDropdown };
